<template>
    <div class="app-grid-container" ref="appGridContainer">
        <AppTile
            v-for="app in apps"
            :key="app.id"
            :app="app"
            :is-active="app.id === activeAppId"
            @click="handleAppClick(app)"
            @contextmenu="handleRightClick($event, app.id)"
            @set-default-app="$emit('set-default-app', $event)"
            @mouseover="$emit('set-active', app.id)"
            :ref="el => { if (el) appTileRefs[app.id] = el }"
        />

        <!-- Default app context menu -->
        <div
            v-if="showContextMenu"
            class="context-menu"
            :style="{ top: contextMenuPos.y + 'px', left: contextMenuPos.x + 'px' }"
        >
            <div class="context-menu-item" @click="setAsDefault">
                Set as default app
            </div>
        </div>
    </div>
</template>

<script setup>
import {ref, computed, onMounted, onUnmounted, nextTick} from 'vue';
import AppTile from './AppTile.vue';
import {params} from "@/systemInfo.js";

const props = defineProps({
    apps: {
        type: Array,
        required: true
    },
    activeAppId: {
        type: Number,
        default: null
    }
});

const emit = defineEmits(['set-active', 'launch-app', 'set-default-app']);

const appTileRefs = ref({});
const showContextMenu = ref(false);
const contextMenuPos = ref({x: 0, y: 0});
const selectedAppId = ref(null);
const appGridContainer = ref(null);
const cols = params.cols || 4; // Default to 4 columns if not specified

// Handle app click
const handleAppClick = (app) => {
    if (app.disabled) return;

    emit('set-active', app.id);
    emit('launch-app', app.id);
};

// Handle right click for context menu
const handleRightClick = (event, id) => {
    event.preventDefault();

    // Set context menu position
    contextMenuPos.value = {
        x: event.clientX,
        y: event.clientY
    };

    // Show context menu and set selected app
    showContextMenu.value = true;
    selectedAppId.value = id;
};

// Set app as default
const setAsDefault = () => {
    if (selectedAppId.value) {
        emit('set-default-app', selectedAppId.value);
    }
    showContextMenu.value = false;
};

// Close context menu when clicking outside
const handleClickOutside = (event) => {
    if (showContextMenu.value) {
        showContextMenu.value = false;
    }
};

// Get app tile element by ID - now async to ensure DOM is updated
const getAppTileElement = async (id) => {
    // Force a nextTick to ensure DOM is updated
    return new Promise(resolve => {
        nextTick(() => {
            if (appTileRefs.value[id] && appTileRefs.value[id].$el) {
                console.log(`Found app tile element for ID ${id}`);
                resolve(appTileRefs.value[id].$el);
            } else {
                console.warn(`App tile element for ID ${id} not found`);
                resolve(null);
            }
        });
    });
};

// Add event listeners
onMounted(() => {
    document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
});

// Expose methods
defineExpose({
    getAppTileElement
});
</script>

<style scoped>
.app-grid-container {
    flex: 1;
    display: grid;
    grid-template-columns: repeat(5, 1fr);
	grid-auto-rows: auto;
	align-items: start;
    gap: var(--spacing-grid);
    padding: var(--spacing-grid);
    overflow-y: auto;
	height: 100%;
	min-height: 0;
	max-height: none;
	box-sizing: border-box;
    scroll-behavior: smooth;
}

.context-menu {
    position: fixed;
    background-color: rgba(30, 30, 30, 0.95);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 0.5rem;
    padding: 0.5rem 0;
    min-width: 180px;
    z-index: 1000;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
}

.context-menu-item {
    padding: 0.75rem 1rem;
    cursor: pointer;
    transition: background-color 0.2s;
}

.context-menu-item:hover {
    background-color: rgba(255, 255, 255, 0.1);
}
</style>